import { Component, computed, effect, Inject, signal, Signal, WritableSignal } from '@angular/core'
import { Customer } from 'src/app/models/customer'
import { CustomerService } from 'src/app/services/customer.service'
import { FilterService } from 'src/app/services/filter.service'
import { ScreenSizeService } from 'src/app/services/screen-size.service'
import { ModalService } from "@gea-waldviertler-website/shop-lib"
import { PreferencesComponent } from 'src/app/customer/preferences/preferences.component'
import { scrollToFirstEl } from "../../helpers";
import { Category } from "@gea-waldviertler-website/gea-models";
import {
  ProductAttributeInterface,
  ProductAttributeOptionInterface
} from "@gea-waldviertler-website/gea-models";
import {
  FilterSelectionShortInterface,
  FilterSelectionsInterface
} from "@gea-waldviertler-website/gea-models";
import { ProductAttributesFilterView } from "../../models/main";

@Component({
    selector: 'gea-shop-filter',
    templateUrl: './filter.component.html',
    standalone: false
})

export class FilterComponent {

  customer: Signal<Customer | undefined>
  selectedCategory?: Category

  viewSelections: WritableSignal<FilterSelectionsInterface> = signal({})
  isPreferenceFilterToggled = computed(() => {
    let currentSelections = this.viewSelections()
    let customerSelections = this.getAvailablePreferenceFilters()
    if (! currentSelections) return false
    for (const [ key, cuSel ] of customerSelections) {
      if (! currentSelections.hasOwnProperty(key)) return false
      for (const cuSelSel of cuSel) {
        for (const cuSelId of cuSelSel.optionIds) {
          if (! currentSelections[key].selections?.includes(cuSelId)) return false
        }
      }
    }
    return true
  })

  productAttributes: Signal<ProductAttributeInterface[]>
  productViewAttributes = computed<ProductAttributesFilterView>(() => {
    return new ProductAttributesFilterView(this.viewSelections())
  })

  hasSelections = computed<boolean>(() => {
    const fs = this.viewSelections()
    if (! fs) return false
    return !! (Object.values(fs).length)
  })

  constructor(
    @Inject(ScreenSizeService) private screenSizeService: ScreenSizeService,
    @Inject(CustomerService) private customerService: CustomerService,
    @Inject(ModalService) private modalService: ModalService,
    public filterService: FilterService,
  ) {
    this.selectedCategory = filterService.titleCategory
    this.modalService = modalService

    this.productAttributes = computed<ProductAttributeInterface[]>(() => {
      const fa = this.filterService.filterAttributes()
      return fa ? [ ... Object.entries(fa).filter(v => v[0] != "farbe")
        .map(i => i[1]) ] : []
    })
    
    this.customer = this.customerService.customer

    this.viewSelections.update((v) => {
      const vs = this.filterService.getFilterSelections()()
      return vs ? { ... vs } : {}
    })

    effect(() => {
      // remove filter key of viewSelections if key does not exist anymore
      // (filter does not exist anymore, e.g. on changing to no shop events)
      const fs = this.filterService.getFilterSelections()()
      if (! fs) return
      const existing = Object.keys(fs)
      this.viewSelections.update((v) => {
        const selected = Object.keys(v)
        for (const k of selected) {
          if (! existing.includes(k)) {
            delete v[k]
          }
        }

        return v ? { ... v } : {}
      })
    })

  }

  clientIsMobile(): boolean {
    return this.screenSizeService.currentDimensionIsSmallerThanEqual('md')
  }

  togglePreferenceFilter(toggle?: boolean) {
    if (!this.customer()?.preferences) {
      return
    }

    if (toggle === undefined) {
      toggle = ! this.isPreferenceFilterToggled()
    }

    let availableAttributes = this.filterService.productAttributes()
    if (! availableAttributes) return
    let customerSelections = this.getAvailablePreferenceFilters()
    let currentSelections = this.viewSelections()

    if (toggle) {
      customerSelections.forEach((options, code) => {
        let optionIds: number[] = []
        for (const option of options) {
          optionIds.push(...option.optionIds)
        }
        if (currentSelections && currentSelections[code]) {
          if (Array.isArray(currentSelections[code].selections)) {
            optionIds = [ ... optionIds, ... currentSelections[code].selections ]
          }
        }
        this.setFilter({attributeCode: code, selections: optionIds})
      })
    } else {
      customerSelections.forEach((options, code) => {
        if (currentSelections && currentSelections[code] && currentSelections[code].selections) {
          let optionIds: number[] = [ ... currentSelections[code].selections ]
          for (const cSel of optionIds) {
            let cuOptionIds: number[] = []
            for (const option of options) {
              cuOptionIds = [ ... cuOptionIds, ... option.optionIds ]
            }
            if (cuOptionIds.includes(cSel)) {
              optionIds = optionIds.filter(v => { return v !== cSel})
            }
          }
          this.setFilter({attributeCode: code, selections: optionIds})
        } else {
          this.setFilter({attributeCode: code, selections: []})
        }
      })
    }
  }

  getAvailablePreferenceFilters(): Map<string, ProductAttributeOptionInterface[]> {
    let availableOptions: Map<string, ProductAttributeOptionInterface[]> = new Map<string, ProductAttributeOptionInterface[]>()
    const prefs = this.customer()?.preferences
    if(! prefs) {
      return availableOptions
    }

    for (const customerPref of prefs) {
      const cPref = (customerPref as any)[1]
      const fa = this.filterService.filterAttributes()
      if (! fa) continue
      const attribute = fa[cPref.attributeCode]
      if(! attribute?.options) {
        continue
      }

      // add available preferences
      for(const opt of attribute.options) {
        if(! cPref.values.includes(opt.title)) {
          continue
        }

        if(availableOptions.has(attribute.code)) {
          availableOptions.get(attribute.code)!.push(opt)
        } else {
          availableOptions.set(attribute.code, [opt])
        }
      }
    }

    return availableOptions
  }

  getPossiblePreferences(): string | null {
    const availableOptions: Map<string, ProductAttributeOptionInterface[]> = this.getAvailablePreferenceFilters()
    let preferenceValues: string [] = []

    availableOptions.forEach((options, code) => {
      options.forEach(opt => {
        preferenceValues.push(opt.title)
      })
    })

    return preferenceValues.join(", ")
  }

  selectPreferences() {
    let that = this

    let modal = this.modalService.show(PreferencesComponent, {
      title: "Meine Größen bearbeiten",
      inputs: {
        submitBtnText: "Anwenden & Speichern",
        customer: this.customer()
      },
      closeBtnText: undefined,
      modalOptions: {
        windowClass: 'preferences-modal',
        size: "lg"
      },
      subscribers: {
        applyPreferencesEvent: function() {
          modal.close()
          that.togglePreferenceFilter(true)
        }
      }
    })
  }

  saveFilter() {
    this.filterService.apply(this.viewSelections())
    this.closeCanvas()
    scrollToFirstEl('.product-list-container', 0)
  }

  resetFilter() {
    this.filterService.reset()
    this.viewSelections.set({})
    this.closeCanvas()
  }

  closeCanvas() {
    this.filterService.hide()
  }

  removeSelection(selection: ProductAttributeOptionInterface, attributeCode: string) {
    const fs = this.viewSelections()
    if (! fs) return
    let filter = fs[attributeCode]
    if (! filter) return
    let optionIds = filter.selections
    
    if (optionIds == undefined) {
      return
    }

    for(let optionId of optionIds) {
      if(selection.optionIds.includes(optionId)) {
        const index = optionIds.indexOf(optionId)
        if (index > -1) {
          optionIds.splice(index, 1)
        }
      }
    }
    filter.selections = optionIds
    this.viewSelections.update(before => {
      if (optionIds.length == 0) {
        delete before[attributeCode]
      } else {
        before[attributeCode].selections = optionIds
      }

      return { ... before }
    })
  }

  getOption(attribute_code: string, optionId: number): ProductAttributeOptionInterface | undefined {
    const fa = this.filterService.filterAttributes()
    if (! fa) return
    const options: ProductAttributeOptionInterface[] | undefined = fa[attribute_code]?.options
    if(options == undefined) {
      return
    }

    for(let option of options) {
      if(option.optionIds.includes(optionId)) {
        return option
      }
    }

    return
  }

  getCategoryPath(cat: Category) {
    return cat.fullPath.replace(new RegExp("/", "g"), "_")
  }

  setFilter(filterSelection: FilterSelectionShortInterface) {
    this.viewSelections.update((fs) => {
      if (! fs) {
        fs = {}
      }

      if (filterSelection.selections?.length) {
        filterSelection.selections = filterSelection.selections.filter((value, index, array) => {
          return array.indexOf(value) === index
        })
        fs[filterSelection.attributeCode] = { ...filterSelection,
          type: FilterService.getFilterType(filterSelection),
          housing: FilterService.getFilterHousing(filterSelection)
        }
      } else {
        if (fs.hasOwnProperty(filterSelection.attributeCode)) {
          delete fs[filterSelection.attributeCode]
        }
      }
      return { ... fs }
    })
  }
}
